/**
 * 旅のしおり詳細ページ
 *
 * ヒーロー / 数字パネル / ルート再生地図 / 日ごとのストーリー / AI 紀行文 で構成する。
 * ハイライトリールとポスター書き出しはここから起動する。
 */

import { CalendarRange, Film, Footprints, ImageIcon, MapPin, Mountain, Route, Sparkles, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'

import type { ImmichAsset } from '@/api/services/immich'
import { Header } from '@/components/shared/header'
import { Badge } from '@/components/ui/badge'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { confirmDialog } from '@/lib/confirm-dialog'
import { shortPrefectureName } from '@/lib/prefectures'
import { ImmichThumbnail } from '@/pages/activity/components/activity-card/immich-thumbnail'
import { PATHS } from '@/routes/paths'
import { useTripStore } from '@/store/useTripStore'

import { TripCoverSheet } from './components/trip-cover-sheet'
import { TripDayTimeline } from './components/trip-day-timeline'
import { TripPeriodSheet } from './components/trip-period-sheet'
import { TripPoster } from './components/trip-poster'
import { TripRouteMap } from './components/trip-route-map'
import { TripStorySection } from './components/trip-story-section'
import { dateRangeLabel, distanceLabel, durationLabel } from './components/trip-utils'

/** 数字パネル 1 マス */
const StatCell = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <div className="flex flex-col items-center gap-0.5 rounded-lg bg-muted/60 p-3">
    <span className="text-muted-foreground">{icon}</span>
    <span className="text-base font-bold tabular-nums">{value}</span>
    <span className="text-[11px] text-muted-foreground">{label}</span>
  </div>
)

export const TripDetailPage = () => {
  const navigate = useNavigate()
  const { trip_id: tripIdParam } = useParams<{ trip_id: string }>()
  const tripId = Number(tripIdParam)

  const detail = useTripStore((s) => s.detail)
  const timeline = useTripStore((s) => s.timeline)
  const route = useTripStore((s) => s.route)
  const isDetailLoading = useTripStore((s) => s.isDetailLoading)
  const isStoryGenerating = useTripStore((s) => s.isStoryGenerating)
  const error = useTripStore((s) => s.error)
  const fetchTripDetail = useTripStore((s) => s.fetchTripDetail)
  const generateStory = useTripStore((s) => s.generateStory)
  const updateTrip = useTripStore((s) => s.updateTrip)
  const deleteTrip = useTripStore((s) => s.deleteTrip)
  const clearDetail = useTripStore((s) => s.clearDetail)
  const clearError = useTripStore((s) => s.clearError)

  const [isPosterOpen, setIsPosterOpen] = useState(false)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [isCoverOpen, setIsCoverOpen] = useState(false)
  // null の間は編集していない
  const [draftTitle, setDraftTitle] = useState<string | null>(null)

  useEffect(() => {
    if (!Number.isFinite(tripId)) return
    void fetchTripDetail(tripId)
    return () => clearDetail()
  }, [tripId, fetchTripDetail, clearDetail])

  useEffect(() => {
    if (!error) return
    toast.error(error)
    clearError()
  }, [error, clearError])

  const days = useMemo(() => timeline?.days ?? [], [timeline])

  // ポスターのコラージュには先頭の写真を使う
  const posterAssetIds = useMemo(() => {
    const assets: ImmichAsset[] = []
    for (const day of days) {
      for (const visit of day.visits) assets.push(...visit.photos)
      assets.push(...day.photos)
    }
    return assets
      .filter((a) => a.type === 'IMAGE')
      .slice(0, 4)
      .map((a) => a.id)
  }, [days])

  const handleDelete = async () => {
    if (
      !(await confirmDialog({
        title: 'この旅の記録を削除しますか？',
        description: '訪問履歴や写真は消えません。',
        destructive: true,
      }))
    )
      return
    await deleteTrip(tripId)
    navigate(PATHS.TRIPS.LIST)
  }

  const handleRename = async () => {
    const trimmed = draftTitle?.trim()
    setDraftTitle(null)
    if (!trimmed || trimmed === detail?.title) return
    await updateTrip(tripId, { title: trimmed })
  }

  const openHighlight = () => navigate(PATHS.TRIPS.HIGHLIGHT.replace(':trip_id', String(tripId)))

  const handleChangePeriod = async (payload: { start_at: string; end_at: string }) => {
    await updateTrip(tripId, payload)
    // 期間に紐づくタイムラインと軌跡を取り直す
    await fetchTripDetail(tripId)
  }

  if (isDetailLoading && !detail) {
    return (
      <div className="flex h-dvh flex-col">
        <Header title="旅のしおり" onClose={() => navigate(PATHS.TRIPS.LIST)} />
        <div className="space-y-3 p-3">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="flex h-dvh flex-col">
        <Header title="旅のしおり" onClose={() => navigate(PATHS.TRIPS.LIST)} />
        <p className="p-8 text-center text-sm text-muted-foreground">旅の記録が見つかりませんでした</p>
      </div>
    )
  }

  const { stats } = detail

  return (
    <div className="flex h-dvh flex-col">
      <Header
        title={detail.title}
        onClose={() => navigate(PATHS.TRIPS.LIST)}
        dropdownSections={[
          {
            type: 'action',
            label: '旅の操作',
            items: [
              {
                key: 'reel',
                label: 'ハイライトリールを再生',
                icon: <Film className="size-4" />,
                onClick: openHighlight,
              },
              {
                key: 'poster',
                label: 'ポスターを書き出す',
                icon: <ImageIcon className="size-4" />,
                onClick: () => setIsPosterOpen(true),
              },
              {
                key: 'period',
                label: '期間を変更',
                icon: <CalendarRange className="size-4" />,
                onClick: () => setIsPeriodOpen(true),
              },
              {
                key: 'delete',
                label: '削除',
                icon: <Trash2 className="size-4" />,
                onClick: () => void handleDelete(),
              },
            ],
          },
        ]}
      />

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3 pb-16">
        {/* ── ヒーロー ─────────────────────────── */}
        <div className="group relative overflow-hidden rounded-xl bg-muted">
          <div className="relative aspect-video w-full">
            {detail.cover_asset_id ? (
              <ImmichThumbnail
                assetId={detail.cover_asset_id}
                alt={detail.title}
                size="preview"
                className="h-full w-full"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <MapPin className="size-10" />
              </div>
            )}
            <div className="absolute inset-0 bg-linear-to-t from-black/85 via-black/25 to-transparent" />
            {/* Notion のようにホバーで出す。ホバーできないタッチ端末では常に出す */}
            {detail.photo_count > 0 && (
              <button
                type="button"
                onClick={() => setIsCoverOpen(true)}
                className="absolute top-3 right-3 flex items-center gap-1.5 rounded-md bg-black/50 px-2.5 py-1.5 text-xs text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 hover:bg-black/70 focus-visible:opacity-100 pointer-coarse:opacity-100"
              >
                <ImageIcon className="size-3.5" />
                カバーを変更
              </button>
            )}
            <div className="absolute inset-x-0 bottom-0 p-4 text-white">
              <Badge variant="secondary" className="mb-1.5 bg-white/20 text-white backdrop-blur-sm">
                {durationLabel(detail)}
              </Badge>
              {draftTitle === null ? (
                <h1 className="wrap-break-word text-xl font-bold">
                  <button
                    type="button"
                    onClick={() => setDraftTitle(detail.title)}
                    aria-label="タイトルを編集"
                    className="-mx-1.5 cursor-pointer rounded-md px-1.5 py-0.5 text-left transition-colors hover:bg-white/20"
                  >
                    {detail.title}
                  </button>
                </h1>
              ) : (
                <input
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onBlur={() => void handleRename()}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    e.currentTarget.blur()
                  }}
                  maxLength={200}
                  autoFocus
                  aria-label="タイトル"
                  className="-mx-1.5 w-[calc(100%+0.75rem)] rounded-md bg-black/40 px-1.5 py-0.5 text-xl font-bold text-white ring-1 ring-white/60 outline-none"
                />
              )}
              <p className="text-xs text-white/80">
                <button
                  type="button"
                  onClick={() => setIsPeriodOpen(true)}
                  aria-label="旅の期間を変更"
                  className="-mx-1.5 cursor-pointer rounded-md px-1.5 py-0.5 text-left transition-colors hover:bg-white/20"
                >
                  {dateRangeLabel(detail)}
                </button>
              </p>
            </div>
          </div>
        </div>

        {stats.prefectures.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {stats.prefectures.map((pref) => (
              <Badge key={pref} variant="outline">
                {shortPrefectureName(pref)}
              </Badge>
            ))}
          </div>
        )}

        {/* ── 数字パネル ────────────────────────── */}
        <div className="grid grid-cols-3 gap-2">
          <StatCell
            icon={<Route className="size-4" />}
            label="移動距離"
            value={distanceLabel(stats.travel_distance_km)}
          />
          <StatCell icon={<Footprints className="size-4" />} label="歩数" value={stats.total_steps.toLocaleString()} />
          <StatCell icon={<MapPin className="size-4" />} label="スポット" value={`${stats.place_count}`} />
          <StatCell
            icon={<ImageIcon className="size-4" />}
            label="写真・動画"
            value={`${stats.photo_count + stats.video_count}`}
          />
          <StatCell
            icon={<Mountain className="size-4" />}
            label="最高標高"
            value={stats.max_altitude_m === null ? '—' : `${Math.round(stats.max_altitude_m)} m`}
          />
          <StatCell icon={<Sparkles className="size-4" />} label="初訪問" value={`${stats.new_place_count}`} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={openHighlight}>
            <Film className="size-4" />
            ハイライト
          </Button>
          <Button variant="outline" onClick={() => setIsPosterOpen(true)}>
            <ImageIcon className="size-4" />
            ポスター
          </Button>
        </div>

        {/* ── ルート ──────────────────────────── */}
        <div>
          <h2 className="mb-2 text-sm font-semibold">旅のルート</h2>
          <TripRouteMap points={route?.points ?? []} visits={days.flatMap((d) => d.visits)} />
        </div>

        {detail.route_plans.length > 0 && (
          <div className="space-y-2">
            <h2 className="text-sm font-semibold">ルートプラン</h2>
            {detail.route_plans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => navigate(PATHS.ROUTE_PLANS.DETAIL.replace(':plan_id', String(plan.id)))}
                className="flex w-full items-center gap-2 rounded-xl border bg-card p-3 text-left"
              >
                <Route className="size-4 shrink-0 text-primary" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{plan.title}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{plan.item_count} スポット</span>
              </button>
            ))}
          </div>
        )}

        {/* ── AI 紀行文 ─────────────────────────── */}
        <TripStorySection
          story={detail.ai_story}
          generatedAt={detail.ai_story_at}
          isGenerating={isStoryGenerating}
          onGenerate={() => void generateStory(tripId)}
        />

        {/* ── 日ごとのストーリー ──────────────────── */}
        <div className="space-y-6">
          <h2 className="text-sm font-semibold">旅のしおり</h2>
          {days.map((day, i) => (
            <TripDayTimeline
              key={day.date}
              day={day}
              dayNumber={i + 1}
              onVisitCreated={() => fetchTripDetail(tripId)}
              coverAssetId={detail.cover_asset_id}
              onSetCover={(assetId) => updateTrip(tripId, { cover_asset_id: assetId })}
            />
          ))}
        </div>
      </div>

      <BottomSheet title="旅のポスター" open={isPosterOpen} onOpenChange={setIsPosterOpen} snapPoints={[0.92]}>
        <div className="px-4 pb-8">
          {isPosterOpen && (
            <TripPoster trip={detail} routePoints={route?.points ?? []} days={days} photoAssetIds={posterAssetIds} />
          )}
        </div>
      </BottomSheet>

      <TripPeriodSheet trip={detail} open={isPeriodOpen} onOpenChange={setIsPeriodOpen} onSubmit={handleChangePeriod} />
      <TripCoverSheet
        open={isCoverOpen}
        onOpenChange={setIsCoverOpen}
        days={days}
        coverAssetId={detail.cover_asset_id}
        hasCustomCover={detail.has_custom_cover}
        onSelect={(assetId) => void updateTrip(tripId, { cover_asset_id: assetId })}
      />
    </div>
  )
}
